import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import Button from 'Components/Button/Button';
import withRouter from 'HOCs/withRouter';
import withConfig from 'HOCs/withConfig/withConfig';
import withTrack from 'HOCs/withTrack/withTrack';
import { SELLER_ACTIONS, PROPS, actionFactory } from 'Helpers/myAdsActionFactory';
import { getMyAdTrackInfo } from 'Helpers/track';
import { MyAdsTracking, MyAdsFlowType } from 'Constants/tracking';
import { canEdit, canDeactivate, canRepublish, canMarkAsSold, isDisabled } from 'Helpers/adActionStatus';
import css from './CallToActions.scss';
import { getLoggedUser } from 'Selectors/user';
import { getItem } from 'Actions/item';
import { setOpenRemoveAfterMarkAsSold } from 'Actions/myads';
import { getOpenRemoveAfterMarkAsSold } from 'Selectors/myads';
import { checkForForbiddenUser } from 'Components/SellerCallToAction/AsyncForbiddenModal/ForbiddenActionHelper';
import withVwo from 'HOCs/withVwo/withVwo';
import { getVariantData } from 'Helpers/vwoTrackParam';
import { EXPERIMENT_AD_POSTING } from '../../../../app/pages/Posting/constants';
import ModalReservation from 'Components/ModalReservation/index';

// Selectors
import {
    getS3ConfigDeactivateConfirmationMessage,
    getS3ConfigDeleteConfirmationMessage
} from 'Selectors/s3config';

export class CallToActions extends Component {
    constructor({ config, openRemoveAfterMarkAsSold }) {
        super();

        this.markAsSoldOnDeletionFlow = config.get('myads', 'markAsSoldOnDeletionFlow').enable;
        this.state = {
            flowType: null,
            action: openRemoveAfterMarkAsSold ? SELLER_ACTIONS.REMOVE : ''
        };
    }

    shouldComponentUpdate(nextProps, nextState) {
        if (this.state.action !== nextState.action
        || this.state.flowType !== nextState.flowType
        || this.props.item.status.status !== nextProps.item.status.status
        || this.props.escrowDetails?.escrow_details_seller !== nextProps.escrowDetails?.escrow_details_seller) {
            return true;
        }
        return false;
    }

    componentWillUnmount() {
        this.props.setOpenRemoveAfterMarkAsSold(false);
    }

    getActions() {
        const { item, intl, escrowDetails } = this.props;

        return {
            [SELLER_ACTIONS.DEACTIVATE]: {
                key: SELLER_ACTIONS.DEACTIVATE,
                allow: canDeactivate(item.status, item.monetizationInfo),
                primary: true,
                text: intl.formatMessage({ id: 'deactivate' }),
                dataAut: 'deactivate-option',
                callToAction: this.toggleActions(escrowDetails?.escrow_details_seller?.is_paid ? SELLER_ACTIONS.DEACTIVATE_RESERVED : SELLER_ACTIONS.DEACTIVATE)
            },
            [SELLER_ACTIONS.REPUBLISH]: {
                key: SELLER_ACTIONS.REPUBLISH,
                allow: canRepublish(item.status),
                primary: true,
                text: intl.formatMessage({ id: 'ad_expiration_republish_button' }),
                dataAut: 'republish-option',
                callToAction: this.toggleActions(SELLER_ACTIONS.REPUBLISH)
            },
            [SELLER_ACTIONS.EDIT_AD]: {
                key: SELLER_ACTIONS.EDIT_AD,
                allow: canEdit(item.status),
                primary: false,
                text: intl.formatMessage({ id: 'editAd' }),
                dataAut: 'edit-option',
                callToAction: this.toggleActions(SELLER_ACTIONS.EDIT_AD)
            },
            [SELLER_ACTIONS.REMOVE]: {
                key: SELLER_ACTIONS.REMOVE,
                allow: true,
                primary: false,
                text: intl.formatMessage({ id: 'removeAd' }),
                dataAut: 'remove-option',
                callToAction: this.toggleActions(escrowDetails?.escrow_details_seller?.is_paid ? SELLER_ACTIONS.REMOVE_RESERVED : SELLER_ACTIONS.ASK_FOR_SOLD)
            }
        };
    }

    trackActions = (event, { itemId = null } = {}, extraParams = {}) => {
        const { track, trackerOrigins, item } = this.props;
        const listingSkParam = itemId ? { item_id: itemId } : null;

        const props = {
            origin: trackerOrigins.ITEM_PAGE,
            ...listingSkParam
        };
        const params = getMyAdTrackInfo(item.status, props);

        track(event, { ...params, ...extraParams });
    };

    editAd = () => {
        const { item } = this.props;

        this.props.router.push({ pathname: `/edit/${item.id}` });
    }

    onDeleteConfirm = () => {
        this.trackActions(MyAdsTracking.AD_REMOVE_COMPLETE, { itemId: this.props.item.id });
        this.props.router.push({
            pathname: '/myads'
        });
    }
    onDeleteError = res => {
        if (checkForForbiddenUser(res)) {
            this.setState({ action: SELLER_ACTIONS.FORBIDDEN });
        }
    }

    onDeactivateConfirm = () => {
        this.trackActions(MyAdsTracking.AD_DEACTIVATE_COMPLETE, { itemId: this.props.item.id });
        this.setState({ action: '' });
        this.props.getItem();
    }

    onMarkAsSoldConfirm = res => {
        if (checkForForbiddenUser(res)) {
            this.setState({ action: SELLER_ACTIONS.FORBIDDEN });
            return;
        }
        if (this.state.flowType === MyAdsFlowType.DELETION) {
            this.props.setOpenRemoveAfterMarkAsSold(true);
        }
    };

    toggleActions = (action, flowType = '') => evt => {
        if (evt) {
            evt.preventDefault();
        }

        switch (action) {
            case SELLER_ACTIONS.REMOVE:
                this.trackActions(MyAdsTracking.AD_REMOVE, { itemId: this.props.item.id });
                this.setState({ action: SELLER_ACTIONS.REMOVE, flowType });
                break;
            case SELLER_ACTIONS.REMOVE_RESERVED:
                this.setState({ action: SELLER_ACTIONS.REMOVE_RESERVED });
                break;
            case SELLER_ACTIONS.ASK_FOR_SOLD: {
                const isAskForSoldFlow = this.markAsSoldOnDeletionFlow && canMarkAsSold(this.props.item.status);

                if (isAskForSoldFlow) {
                    this.setState({ action: SELLER_ACTIONS.ASK_FOR_SOLD });
                }
                else {
                    this.trackActions(MyAdsTracking.AD_REMOVE, { itemId: this.props.item.id });
                    this.setState({ action: SELLER_ACTIONS.REMOVE, flowType: MyAdsFlowType.DELETION });
                }
                break;
            }
            case SELLER_ACTIONS.MARK_AS_SOLD:
                this.setState({ action: SELLER_ACTIONS.MARK_AS_SOLD, flowType });
                break;
            case SELLER_ACTIONS.EDIT_AD:
                this.trackActions(MyAdsTracking.AD_EDIT, { itemId: this.props.item.id }, { ...getVariantData(this.props.vwoClientInstance, EXPERIMENT_AD_POSTING) });
                this.editAd();
                break;
            case SELLER_ACTIONS.REPUBLISH: {
                const event = isDisabled(this.props.item.status) ? MyAdsTracking.AD_REACTIVATE : MyAdsTracking.AD_REPUBLISH;

                this.trackActions(event, { itemId: this.props.item.id });
                this.editAd();
            }
                break;
            case SELLER_ACTIONS.DEACTIVATE:
                this.trackActions(MyAdsTracking.AD_DEACTIVATE, { itemId: this.props.item.id });
                this.setState({ action: SELLER_ACTIONS.DEACTIVATE });
                break;
            case SELLER_ACTIONS.DEACTIVATE_RESERVED:
                this.setState({ action: SELLER_ACTIONS.DEACTIVATE_RESERVED });
                break;
            default:
                this.setState({ action: null });
                break;
        }
    };

    getModalComponent = action => {
        const { item, loggedUser } = this.props;
        const actionMap = {
            [SELLER_ACTIONS.ASK_FOR_SOLD]: {
                [PROPS.open]: true,
                [PROPS.onAccept]: this.toggleActions(SELLER_ACTIONS.MARK_AS_SOLD, MyAdsFlowType.DELETION),
                [PROPS.onDecline]: this.toggleActions(SELLER_ACTIONS.REMOVE, MyAdsFlowType.DELETION)
            },
            [SELLER_ACTIONS.REMOVE]: {
                [PROPS.open]: true,
                [PROPS.userId]: loggedUser.id,
                [PROPS.itemId]: item.id,
                [PROPS.track]: this.trackActions,
                [PROPS.onAccept]: this.onDeleteConfirm,
                [PROPS.onError]: this.onDeleteError,
                [PROPS.onDecline]: this.toggleActions()
            },
            [SELLER_ACTIONS.MARK_AS_SOLD]: {
                [PROPS.images]: item.images,
                [PROPS.title]: item.title,
                [PROPS.price]: item.price && item.price.value && item.price.value.display,
                [PROPS.itemId]: item.id,
                [PROPS.callback]: this.onMarkAsSoldConfirm,
                [PROPS.onClick]: this.toggleActions(),
                [PROPS.track]: this.trackActions
            },
            [SELLER_ACTIONS.DEACTIVATE]: {
                [PROPS.open]: true,
                [PROPS.itemId]: item.id,
                [PROPS.onAccept]: this.onDeactivateConfirm,
                [PROPS.onDecline]: this.toggleActions()
            },
            [SELLER_ACTIONS.FORBIDDEN]: {
                [PROPS.open]: true,
                [PROPS.onDecline]: this.toggleActions(),
                [PROPS.onAccept]: this.toggleActions()
            }
        };
        const component = actionFactory(action, actionMap[action]);

        return component;
    }

    render() {
        const actions = this.getActions();
        const [primaryButtons, secondaryButtons] = Object.keys(actions).reduce((result, key) => {
            if (actions[key].allow) {
                result[actions[key].primary ? 0 : 1].push(actions[key]);
            }
            return result;
        }, [[], []]);

        let modalComponent = null;

        if (this.state.action) {
            modalComponent = this.getModalComponent(this.state.action, this.state.flowType);
        }

        const { 'data-aut-id': dataAutId } = this.props;

        let modalInfoReservation = null;

        if (this.props.escrowDetails?.escrow_details_seller?.is_paid) {
            switch (this.state.action) {
                case SELLER_ACTIONS.DEACTIVATE_RESERVED:
                    modalInfoReservation = (
                        <ModalReservation
                            title={ this.props.intl.formatMessage({ id: 'deactiveConfirmation', defaultMessage: 'Konfirmasi Nonaktif' }) }
                            description={ this.props.getS3ConfigDeactivateConfirmationMessage }
                            onConfirm={ this.toggleActions(SELLER_ACTIONS.DEACTIVATE) }
                            onCancel={ this.toggleActions() }
                        />
                    );
                    break;
                case SELLER_ACTIONS.REMOVE_RESERVED:
                    modalInfoReservation = (
                        <ModalReservation
                            title={ this.props.intl.formatMessage({ id: 'deleteConfirmation', defaultMessage: 'Konfirmasi Hapus' }) }
                            description={ this.props.getS3ConfigDeleteConfirmationMessage }
                            onConfirm={ this.toggleActions(SELLER_ACTIONS.REMOVE) }
                            onCancel={ this.toggleActions() }
                        />
                    );
                    break;
                default:
                    break;
            }
        }

        return (
            <Fragment>
                <div className={ css.sellerActions } data-aut-id={ dataAutId }>
                    { primaryButtons.map(action => (
                        <Button
                            key={ action.key }
                            className={ css.sellerActionBtn }
                            type="btnMain"
                            onClick={ action.callToAction }
                            data-aut-id={ action.dataAut }
                            fixed={ true }
                        >{ action.text }</Button>
                    ))}
                    <div className={ css.secondaryCta }>
                        { secondaryButtons.map(action => (
                            <Button
                                key={ action.key }
                                className={ css.sellerActionBtn }
                                type="btnOutline"
                                onClick={ action.callToAction }
                                data-aut-id={ action.dataAut }
                            >{ action.text }</Button>
                        ))}
                    </div>
                </div>
                { modalComponent }
                { modalInfoReservation }
            </Fragment>);
    }
}

CallToActions.propTypes = {
    'data-aut-id': PropTypes.string,
    item: PropTypes.shape({
        id: PropTypes.string.isRequired,
        status: PropTypes.object.isRequired,
        monetizationInfo: PropTypes.object,
        images: PropTypes.object,
        title: PropTypes.string,
        price: PropTypes.object,
        reservation: PropTypes.object,
        escrow_details: PropTypes.object,
        escrow_details_seller: PropTypes.object
    }).isRequired,
    intl: PropTypes.shape({
        formatMessage: PropTypes.func.isRequired
    }).isRequired,
    config: PropTypes.object.isRequired,
    track: PropTypes.func.isRequired,
    trackerOrigins: PropTypes.object.isRequired,
    router: PropTypes.object.isRequired,
    loggedUser: PropTypes.object.isRequired,
    /** required: get item info after an action. ex. deactivate  */
    getItem: PropTypes.func.isRequired,
    /** used to display the deletion modal after mark as sold.
    * Because the mark as sold execute a getItem the whole component is re-rendered
    * and the state reference is lost, we manage this behaviour through the store.
    * (TODO: check an improvement for this behaviour)
    */
    openRemoveAfterMarkAsSold: PropTypes.bool,
    setOpenRemoveAfterMarkAsSold: PropTypes.func.isRequired,
    vwoClientInstance: PropTypes.shape({
        activate: PropTypes.func.isRequired,
        track: PropTypes.func.isRequired
    }),
    getS3ConfigDeactivateConfirmationMessage: PropTypes.string,
    getS3ConfigDeleteConfirmationMessage: PropTypes.string,
    escrowDetails: PropTypes.object
};

CallToActions.defaultProps = {
    'data-aut-id': 'sellerCallToActions',
    openRemoveAfterMarkAsSold: false
};

export const mapStateToProps = state => ({
    loggedUser: getLoggedUser(state),
    openRemoveAfterMarkAsSold: getOpenRemoveAfterMarkAsSold(state),
    getS3ConfigDeactivateConfirmationMessage: getS3ConfigDeactivateConfirmationMessage(state),
    getS3ConfigDeleteConfirmationMessage: getS3ConfigDeleteConfirmationMessage(state)
});

export const mapDispatchToProps = (dispatch, { item }) => ({
    getItem: () => dispatch(getItem(item.id)),
    setOpenRemoveAfterMarkAsSold: openRemoveAfterMarkAsSold => dispatch(setOpenRemoveAfterMarkAsSold(openRemoveAfterMarkAsSold))
});

export default compose(
    injectIntl,
    withTrack,
    withConfig,
    withRouter,
    withVwo,
    connect(mapStateToProps, mapDispatchToProps)
)(CallToActions);
